/**
 * DAAM Leads — Campaign Editor page.
 *
 * Client-only route at /app/leads/campaigns/:id/edit.
 *
 * Lets the business owner turn a campaign into a full landing page:
 *   • headline + offer description
 *   • up to 4 promotional images + 1 short video (uploaded to Emergent
 *     Object Storage via the backend)
 *   • its own slug (nested as /l/{businessSlug}/{campaignSlug})
 *   • per-campaign CTA destinations (WhatsApp, call, order URL,
 *     website URL) — empty means "use the business page's default"
 *   • per-CTA enable toggles + a primary CTA that renders large
 *   • publish / draft toggle (separate from is_active tracking)
 *
 * Layout is a single-column editor on mobile with a sticky share bar
 * at the top; on lg+ it splits into a two-column grid (editor on the
 * right, live preview card on the left in RTL — the preview mirrors
 * what visitors will see on /l/{business}/{campaign}).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import AppShell from "@/components/layout/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button }   from "@/components/ui/button";
import { Input }    from "@/components/ui/input";
import { Label }    from "@/components/ui/label";
import { Switch }   from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge }    from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowRight, Check, X, Copy, Eye, EyeOff, ImagePlus, Film, Trash2,
  Loader2, MessageCircle, Phone, ShoppingBag, Globe2, Megaphone,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const MAX_IMAGES   = 4;
const IMG_MAX_MB   = 2;
const VIDEO_MAX_MB = 25;
const SLUG_RE      = /^[a-z0-9](?:[a-z0-9]|-(?=[a-z0-9])){1,38}[a-z0-9]$|^[a-z0-9]{3}$/;

const CTA_META = {
  whatsapp: { label: "واتساب",    icon: MessageCircle, accent: "bg-[#25D366] hover:bg-[#1EBE5D]" },
  call:     { label: "اتصال",     icon: Phone,         accent: "bg-neutral-900 hover:bg-neutral-800" },
  order:    { label: "اطلب الآن", icon: ShoppingBag,   accent: "bg-amber-600 hover:bg-amber-700" },
  website:  { label: "الموقع",    icon: Globe2,        accent: "bg-neutral-900 hover:bg-neutral-800" },
};

function humanErr(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  return d?.message_ar || d?.message_en || formatApiError(e) || "حدث خطأ";
}

export default function CampaignEditorPage() {
  const { id } = useParams();
  const nav    = useNavigate();

  const [state, setState] = useState({ status: "loading" });
  const [c, setC]         = useState(null);           // the campaign-landing payload
  const [businessSlug, setBusinessSlug] = useState("");

  // ---- form (controlled copies of the fields that we edit locally) ----
  const [headline,  setHeadline]  = useState("");
  const [offerDesc, setOfferDesc] = useState("");
  const [slug,      setSlug]      = useState("");
  const [wa,        setWA]        = useState("");
  const [call,      setCall]      = useState("");
  const [orderUrl,  setOrderUrl]  = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [btns,      setBtns]      = useState({ whatsapp: true, call: true, order: true, website: true });
  const [primary,   setPrimary]   = useState("");

  const [slugCheck, setSlugCheck] = useState({ status: "idle", avail: null });
  const [saving,    setSaving]    = useState(false);
  const [publishing, setPublishing] = useState(false);
  // Slug-change confirmation. The campaign public URL contains the
  // slug, so renaming it breaks every link already shared on ads /
  // socials / WhatsApp — we gate the save behind an explicit
  // acknowledgement.
  const [slugConfirm, setSlugConfirm] = useState(null); // {oldSlug, newSlug} | null

  const fileImg = useRef(null);
  const fileVid = useRef(null);
  const [uploading, setUploading] = useState(false);

  // ------------------------- Load ------------------------------------
  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const [landing, mine] = await Promise.all([
        api.get(`/leads/campaigns/${id}/landing`),
        api.get("/leads/mine"),
      ]);
      const d = landing.data;
      setC(d);
      setBusinessSlug(mine.data?.slug || "");
      setHeadline(d.headline || "");
      setOfferDesc(d.offer_description || "");
      setSlug(d.slug || "");
      setWA(d.whatsapp_number || "");
      setCall(d.call_number || "");
      setOrderUrl(d.order_url || "");
      setWebsiteUrl(d.website_url || "");
      setBtns(d.buttons_enabled || { whatsapp: true, call: true, order: true, website: true });
      setPrimary(d.primary_cta || "");
      setState({ status: "ok" });
    } catch (e) {
      setState({ status: "error", msg: humanErr(e) });
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // ------------------------- Slug probe ------------------------------
  useEffect(() => {
    if (!slug || slug === c?.slug) { setSlugCheck({ status: "idle", avail: null }); return; }
    if (!SLUG_RE.test(slug))       { setSlugCheck({ status: "bad",  avail: false }); return; }
    const t = setTimeout(async () => {
      setSlugCheck({ status: "checking", avail: null });
      try {
        const { data } = await api.get(`/leads/campaigns/slug-check?slug=${encodeURIComponent(slug)}&id=${encodeURIComponent(id)}`);
        setSlugCheck({ status: "done", avail: !!data.available });
      } catch {
        setSlugCheck({ status: "done", avail: false });
      }
    }, 400);
    return () => clearTimeout(t);
  }, [slug, c, id]);

  // ------------------------- Save ------------------------------------
  // Public API: components call `requestSave()`; it will either save
  // immediately or open the slug-change confirmation first.
  async function requestSave() {
    const oldSlug = (c?.slug || "").trim();
    const newSlug = (slug || "").trim();
    // Only intercept when a saved slug is being replaced with a
    // *different* non-empty value. First-time slug assignment and
    // no-op saves pass straight through.
    if (oldSlug && newSlug && oldSlug !== newSlug) {
      setSlugConfirm({ oldSlug, newSlug });
      return;
    }
    try { await save(); } catch { /* save surfaced the toast */ }
  }

  async function confirmSlugChange() {
    const pending = slugConfirm;
    setSlugConfirm(null);
    if (!pending) return;
    try { await save(); } catch { /* save surfaced the toast */ }
  }

  async function save(extraFields = {}) {
    setSaving(true);
    const body = {
      headline, offer_description: offerDesc,
      slug: slug || null,
      whatsapp_number: wa,
      call_number:     call,
      order_url:       orderUrl,
      website_url:     websiteUrl,
      buttons_enabled: btns,
      primary_cta:     primary,
      ...extraFields,
    };
    try {
      const { data } = await api.patch(`/leads/campaigns/${id}/landing`, body);
      setC(data);
      toast.success("تم الحفظ");
      return data;
    } catch (e) {
      toast.error(humanErr(e));
      throw e;
    } finally {
      setSaving(false);
    }
  }

  async function togglePublish() {
    if (publishing) return;
    // Publishing/unpublishing while the slug ALSO changed would silently
    // break the public URL. Force the user through the warning first.
    const oldSlug = (c?.slug || "").trim();
    const newSlug = (slug || "").trim();
    if (oldSlug && newSlug && oldSlug !== newSlug) {
      setSlugConfirm({ oldSlug, newSlug });
      return;
    }
    setPublishing(true);
    try {
      const data = await save({ is_published: !c.is_published });
      toast.success(data.is_published ? "تم نشر الحملة" : "أصبحت الحملة مسودة");
    } catch { /* save surfaced the error */ }
    finally { setPublishing(false); }
  }

  // ------------------------- Media -----------------------------------
  async function uploadImage(f) {
    if (!f) return;
    if (f.size > IMG_MAX_MB * 1024 * 1024) { toast.error(`الصورة أكبر من ${IMG_MAX_MB}MB`); return; }
    const fd = new FormData(); fd.append("file", f);
    setUploading(true);
    try {
      const { data } = await api.post(`/leads/campaigns/${id}/media/image`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setC((prev) => ({ ...prev, media: data.media }));
      toast.success("تم رفع الصورة");
    } catch (e) { toast.error(humanErr(e)); }
    finally { setUploading(false); if (fileImg.current) fileImg.current.value = ""; }
  }
  async function uploadVideo(f) {
    if (!f) return;
    if (f.size > VIDEO_MAX_MB * 1024 * 1024) { toast.error(`الفيديو أكبر من ${VIDEO_MAX_MB}MB`); return; }
    const fd = new FormData(); fd.append("file", f);
    setUploading(true);
    try {
      const { data } = await api.post(`/leads/campaigns/${id}/media/video`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setC((prev) => ({ ...prev, media: data.media }));
      toast.success("تم رفع الفيديو");
    } catch (e) { toast.error(humanErr(e)); }
    finally { setUploading(false); if (fileVid.current) fileVid.current.value = ""; }
  }
  async function deleteMedia(mid) {
    try {
      const { data } = await api.delete(`/leads/campaigns/${id}/media/${mid}`);
      setC((prev) => ({ ...prev, media: data.media }));
      toast.success("تم الحذف");
    } catch (e) { toast.error(humanErr(e)); }
  }

  // ------------------------- Share -----------------------------------
  // Note: the shareable URL deliberately points at /api/leads/share/…
  // (not /l/…). The share route is served by FastAPI with the campaign's
  // own Open Graph tags so WhatsApp/Facebook/Twitter show the
  // ADVERTISER'S headline + image when the link is shared. Human
  // browsers are instantly redirected to /l/{b}/{c} so they still see
  // the real campaign page.
  const shareUrl = useMemo(() => {
    if (!businessSlug || !slug) return "";
    return `${window.location.origin}/api/leads/share/${businessSlug}/${slug}`;
  }, [businessSlug, slug]);

  function copyShare() {
    if (!shareUrl) { toast.error("أدخل رابط الحملة أولاً"); return; }
    navigator.clipboard.writeText(shareUrl).then(
      () => toast.success("تم نسخ الرابط"),
      () => toast.error("تعذّر النسخ"),
    );
  }

  // ------------------------- Render ---------------------------------
  if (state.status === "loading") {
    return (
      <AppShell>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
        </div>
      </AppShell>
    );
  }
  if (state.status === "error") {
    return (
      <AppShell>
        <div className="max-w-md mx-auto py-10 text-center">
          <p className="text-sm text-red-500">{state.msg}</p>
          <Link to="/app/leads" className="mt-4 inline-block text-sm underline">
            العودة إلى DAAM Leads
          </Link>
        </div>
      </AppShell>
    );
  }

  const images = (c?.media || []).filter((m) => m.kind === "image");
  const video  = (c?.media || []).find((m) => m.kind === "video");
  const canPublish = !!headline?.trim() && SLUG_RE.test(slug || "");

  return (
    <AppShell>
      <div data-testid="campaign-editor-page" dir="rtl" className="pb-24">
        {/* ---------- Sticky header ---------- */}
        <div className="sticky top-0 z-20 bg-white/85 backdrop-blur border-b border-neutral-200">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <Link to="/app/leads"
                    data-testid="campaign-editor-back"
                    className="text-neutral-500 hover:text-neutral-900">
                <ArrowRight className="h-5 w-5 rtl:rotate-180" />
              </Link>
              <div className="min-w-0">
                <h1 className="text-base sm:text-lg font-semibold truncate">
                  <Megaphone className="inline h-4 w-4 me-1.5 text-amber-600" />
                  {c?.name || "تحرير الحملة"}
                </h1>
                <div className="text-[11px] text-neutral-500 truncate">
                  {shareUrl || "اضبط رابط الحملة لتفعيل المشاركة"}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm"
                      onClick={copyShare}
                      data-testid="campaign-editor-copy-share"
                      disabled={!shareUrl}>
                <Copy className="h-3.5 w-3.5 me-1.5" /> نسخ الرابط
              </Button>
              <Button size="sm" onClick={requestSave}
                      data-testid="campaign-editor-save-btn"
                      disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "حفظ"}
              </Button>
              <Button size="sm" variant={c?.is_published ? "secondary" : "default"}
                      onClick={togglePublish}
                      data-testid="campaign-editor-publish-btn"
                      disabled={publishing || !canPublish}>
                {publishing ? <Loader2 className="h-4 w-4 animate-spin" />
                            : (c?.is_published ? (<><EyeOff className="h-4 w-4 me-1.5"/>إيقاف النشر</>)
                                               : (<><Eye    className="h-4 w-4 me-1.5"/>نشر</>))}
              </Button>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* ----- Editor column ----- */}
          <div className="lg:col-span-3 space-y-5" dir="rtl">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">تفاصيل الإعلان</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-xs">العنوان الإعلاني *</Label>
                  <Input value={headline}
                         data-testid="campaign-editor-headline"
                         maxLength={120}
                         placeholder="مثال: عرض اليوم الوطني — خصم 30٪ على كل الهدايا"
                         onChange={(e) => setHeadline(e.target.value)}
                         className="mt-1" />
                  <div className="text-[11px] text-neutral-400 mt-1 text-end">
                    {headline.length}/120
                  </div>
                </div>
                <div>
                  <Label className="text-xs">وصف العرض / المنتج</Label>
                  <Textarea value={offerDesc}
                            data-testid="campaign-editor-offer"
                            maxLength={800}
                            rows={4}
                            placeholder="اشرح ما الذي يجعل هذا العرض مميزًا — السعر، الكمية، المدة، شروط الاستفادة…"
                            onChange={(e) => setOfferDesc(e.target.value)}
                            className="mt-1 resize-none" />
                  <div className="text-[11px] text-neutral-400 mt-1 text-end">
                    {offerDesc.length}/800
                  </div>
                </div>
                <div>
                  <Label className="text-xs">رابط الحملة (slug)</Label>
                  <div className="mt-1 flex items-center gap-2">
                    <div className="text-[11px] text-neutral-400 whitespace-nowrap" dir="ltr">
                      /l/{businessSlug || "{business}"}/
                    </div>
                    <Input value={slug}
                           dir="ltr"
                           data-testid="campaign-editor-slug"
                           onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                           placeholder="ramadan-promo"
                           className="font-mono text-sm" />
                    {slug && slugCheck.status === "checking" && (
                      <Loader2 className="h-4 w-4 animate-spin text-neutral-400" />
                    )}
                    {slug && slugCheck.status === "done" && slugCheck.avail && (
                      <Check className="h-4 w-4 text-green-600" />
                    )}
                    {slug && (slugCheck.status === "bad" ||
                              (slugCheck.status === "done" && slugCheck.avail === false)) && (
                      <X className="h-4 w-4 text-red-500" />
                    )}
                  </div>
                  <div className="text-[11px] text-neutral-500 mt-1">
                    3–40 حرفًا — صغيرة، أرقام، شرطات فقط. فريد ضمن حسابك.
                  </div>
                  {c?.slug && slug && slug !== c.slug && (
                    <div data-testid="campaign-editor-slug-warning"
                         className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] leading-6 text-amber-900">
                      <b>تنبيه:</b> تغيير الرابط من
                      <span className="mx-1 inline-block font-mono bg-white/70 rounded px-1.5 py-0.5">{c.slug}</span>
                      إلى
                      <span className="mx-1 inline-block font-mono bg-white/70 rounded px-1.5 py-0.5">{slug}</span>
                      سيوقف عمل الرابط القديم مباشرةً. أي إعلان أو منشور أو
                      رسالة واتساب تحتوي الرابط القديم ستفتح صفحة «غير متاحة».
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* ---- Media ---- */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">الصور والفيديو</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                  {images.map((m) => (
                    <div key={m.id}
                         data-testid={`campaign-editor-image-${m.id}`}
                         className="relative aspect-square rounded-lg overflow-hidden bg-neutral-100 ring-1 ring-neutral-200 group">
                      <img src={m.url} alt="" className="w-full h-full object-cover" />
                      <button type="button"
                              onClick={() => deleteMedia(m.id)}
                              data-testid={`campaign-editor-image-remove-${m.id}`}
                              className="absolute top-1.5 end-1.5 w-7 h-7 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition grid place-items-center">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  {images.length < MAX_IMAGES && (
                    <button type="button"
                            onClick={() => fileImg.current?.click()}
                            disabled={uploading}
                            data-testid="campaign-editor-add-image-btn"
                            className="aspect-square rounded-lg border-2 border-dashed border-neutral-300 flex flex-col items-center justify-center text-neutral-500 hover:text-neutral-800 hover:border-neutral-400 transition disabled:opacity-50">
                      {uploading ? <Loader2 className="h-5 w-5 animate-spin" />
                                 : <ImagePlus className="h-5 w-5" />}
                      <div className="text-[11px] mt-1">أضف صورة</div>
                    </button>
                  )}
                </div>
                <div className="text-[11px] text-neutral-500">
                  حتى {MAX_IMAGES} صور · PNG/JPG/WebP · {IMG_MAX_MB}MB لكل صورة.
                </div>
                <input ref={fileImg} type="file" accept="image/png,image/jpeg,image/webp"
                       className="hidden"
                       onChange={(e) => uploadImage(e.target.files?.[0])} />

                <Separator />

                <div>
                  <Label className="text-xs">فيديو ترويجي (اختياري)</Label>
                  {video ? (
                    <div data-testid="campaign-editor-video"
                         className="mt-2 relative rounded-lg overflow-hidden bg-black">
                      <video src={video.url} controls className="w-full max-h-72 bg-black" />
                      <button type="button"
                              onClick={() => deleteMedia(video.id)}
                              data-testid="campaign-editor-video-remove"
                              className="absolute top-2 end-2 w-7 h-7 rounded-full bg-black/70 text-white grid place-items-center">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : (
                    <button type="button"
                            onClick={() => fileVid.current?.click()}
                            disabled={uploading}
                            data-testid="campaign-editor-add-video-btn"
                            className="mt-2 w-full rounded-lg border-2 border-dashed border-neutral-300 py-6 flex flex-col items-center justify-center text-neutral-500 hover:text-neutral-800 hover:border-neutral-400 transition disabled:opacity-50">
                      {uploading ? <Loader2 className="h-5 w-5 animate-spin" />
                                 : <Film className="h-5 w-5" />}
                      <div className="text-xs mt-1">ارفع فيديو MP4 (حتى {VIDEO_MAX_MB}MB، 60 ثانية)</div>
                    </button>
                  )}
                  <input ref={fileVid} type="file" accept="video/mp4"
                         className="hidden"
                         onChange={(e) => uploadVideo(e.target.files?.[0])} />
                </div>
              </CardContent>
            </Card>

            {/* ---- CTAs ---- */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">أزرار الإجراء (CTA)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs text-neutral-500">
                  اترك أي حقل فارغًا لاستخدام القيمة الافتراضية من صفحة نشاطك.
                  لن يُعرض أي زر إذا لم يكن له وجهة مُحدّدة.
                </p>
                <CTAField label="رقم واتساب" dir="ltr"
                          value={wa} onChange={setWA}
                          placeholder="+9665XXXXXXXX"
                          testid="campaign-editor-wa"
                          enabled={btns.whatsapp}
                          onEnabledChange={(v) => setBtns({ ...btns, whatsapp: v })} />
                <CTAField label="رقم الاتصال" dir="ltr"
                          value={call} onChange={setCall}
                          placeholder="+9665XXXXXXXX"
                          testid="campaign-editor-call"
                          enabled={btns.call}
                          onEnabledChange={(v) => setBtns({ ...btns, call: v })} />
                <CTAField label="رابط الطلب" dir="ltr"
                          value={orderUrl} onChange={setOrderUrl}
                          placeholder="https://example.com/order"
                          testid="campaign-editor-order"
                          enabled={btns.order}
                          onEnabledChange={(v) => setBtns({ ...btns, order: v })} />
                <CTAField label="رابط الموقع" dir="ltr"
                          value={websiteUrl} onChange={setWebsiteUrl}
                          placeholder="https://example.com"
                          testid="campaign-editor-website"
                          enabled={btns.website}
                          onEnabledChange={(v) => setBtns({ ...btns, website: v })} />

                <div>
                  <Label className="text-xs">الإجراء الرئيسي (اختياري)</Label>
                  <div className="mt-1 grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[["", "لا شيء"], ["whatsapp", "واتساب"], ["call", "اتصال"],
                      ["order", "الطلب"], ["website", "الموقع"]].slice(0, 5).map(([key, label]) => (
                      <button key={key || "none"}
                              type="button"
                              onClick={() => setPrimary(key)}
                              data-testid={`campaign-editor-primary-${key || "none"}`}
                              className={`text-xs py-2 rounded-md border transition ${
                                primary === key
                                  ? "bg-amber-50 border-amber-500 text-amber-800"
                                  : "border-neutral-200 text-neutral-700 hover:border-neutral-300"
                              }`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* ----- Live preview ----- */}
          <div className="lg:col-span-2">
            <div className="lg:sticky lg:top-20">
              <PreviewCard
                businessName={c?.business_name_hint || ""}
                headline={headline}
                offerDesc={offerDesc}
                images={images}
                video={video}
                btns={btns}
                primary={primary}
                wa={wa} call={call} orderUrl={orderUrl} websiteUrl={websiteUrl}
              />
            </div>
          </div>
        </div>

        {/* ---- Slug-change confirmation ---- */}
        <AlertDialog open={!!slugConfirm} onOpenChange={(o) => !o && setSlugConfirm(null)}>
          <AlertDialogContent dir="rtl" data-testid="campaign-editor-slug-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-start">
                تأكيد تغيير رابط الحملة
              </AlertDialogTitle>
              <AlertDialogDescription className="text-start leading-7">
                سيتغيّر رابط المشاركة من
                <span className="mx-1 inline-block font-mono bg-neutral-100 rounded px-1.5 py-0.5">
                  /l/{businessSlug}/{slugConfirm?.oldSlug}
                </span>
                إلى
                <span className="mx-1 inline-block font-mono bg-amber-50 border border-amber-300 rounded px-1.5 py-0.5">
                  /l/{businessSlug}/{slugConfirm?.newSlug}
                </span>
                . بعد الحفظ:
                <ul className="list-disc ps-5 mt-2 space-y-1 text-[13px]">
                  <li>أي إعلان مموَّل أو منشور أو رسالة واتساب تحتوي الرابط
                      القديم ستفتح صفحة «غير متاحة» (404).</li>
                  <li>سجلات الأحداث والتحليلات القديمة ستبقى مرتبطة بالحملة
                      كما هي — فقط عنوان الرابط يتغير.</li>
                  <li>لا توجد إعادة توجيه تلقائية من الرابط القديم للجديد.</li>
                </ul>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-row-reverse gap-2">
              <AlertDialogAction onClick={confirmSlugChange}
                                 data-testid="campaign-editor-slug-confirm-ok"
                                 className="bg-amber-600 hover:bg-amber-700">
                نعم، غيّر الرابط
              </AlertDialogAction>
              <AlertDialogCancel data-testid="campaign-editor-slug-confirm-cancel">
                إلغاء
              </AlertDialogCancel>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AppShell>
  );
}

// ---------------------- Sub components ----------------------
function CTAField({ label, value, onChange, placeholder, testid, enabled, onEnabledChange, dir }) {
  return (
    <div className="flex items-center gap-3">
      <Switch checked={enabled}
              onCheckedChange={onEnabledChange}
              data-testid={`${testid}-enabled`} />
      <div className="flex-1">
        <Label className="text-xs">{label}</Label>
        <Input value={value || ""}
               dir={dir}
               data-testid={`${testid}-input`}
               onChange={(e) => onChange(e.target.value)}
               placeholder={placeholder}
               className="mt-1 text-sm font-mono" />
      </div>
    </div>
  );
}

function PreviewCard({ headline, offerDesc, images, video, btns, primary, wa, call, orderUrl, websiteUrl }) {
  // CTA availability — matches backend precedence (campaign override else page fallback).
  const ctaOrder = ["whatsapp", "call", "order", "website"];
  const vals = { whatsapp: wa, call, order: orderUrl, website: websiteUrl };
  const avail = ctaOrder.filter((k) => btns[k] && vals[k]);
  const primaryKey = primary && avail.includes(primary) ? primary : null;
  const secondaryKeys = avail.filter((k) => k !== primaryKey);

  return (
    <Card data-testid="campaign-editor-preview">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-neutral-500 font-normal flex items-center gap-2">
          <Eye className="h-3.5 w-3.5" /> معاينة مباشرة
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div dir="rtl" className="rounded-2xl overflow-hidden bg-white ring-1 ring-neutral-200">
          {/* Hero media */}
          <div className="aspect-[4/3] bg-neutral-100 flex items-center justify-center text-neutral-400 text-sm overflow-hidden">
            {video ? (
              <video src={video.url} controls className="w-full h-full object-cover bg-black" />
            ) : images[0] ? (
              <img src={images[0].url} alt="" className="w-full h-full object-cover" />
            ) : (
              <span>أضف صورة أو فيديو</span>
            )}
          </div>
          {/* Thumbnails */}
          {images.length > 1 && (
            <div className="flex gap-1.5 p-2 bg-neutral-50">
              {images.slice(1).map((m) => (
                <img key={m.id} src={m.url} alt="" className="h-14 w-14 object-cover rounded-md ring-1 ring-neutral-200" />
              ))}
            </div>
          )}
          {/* Copy */}
          <div className="p-5">
            <h2 className="font-semibold text-lg leading-snug">
              {headline || "عنوان الإعلان يظهر هنا"}
            </h2>
            {offerDesc && (
              <p className="mt-2.5 text-sm text-neutral-700 leading-6 whitespace-pre-wrap">
                {offerDesc}
              </p>
            )}
            {/* CTAs */}
            <div className="mt-5 space-y-2">
              {avail.length === 0 && (
                <div className="text-xs text-neutral-400 text-center py-2">
                  أضف وجهة واحدة على الأقل لعرض أزرار الإجراء
                </div>
              )}
              {primaryKey && (
                <PreviewButton k={primaryKey} big />
              )}
              {secondaryKeys.map((k) => (<PreviewButton key={k} k={k} />))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PreviewButton({ k, big }) {
  const m = CTA_META[k];
  const Icon = m.icon;
  return (
    <div className={`w-full rounded-xl text-white flex items-center justify-center gap-2 ${m.accent} ${big ? "py-4 text-base font-semibold" : "py-3 text-sm"}`}>
      <Icon className={big ? "h-5 w-5" : "h-4 w-4"} />
      <span>{m.label}</span>
    </div>
  );
}
